import {useCallback, useMemo, useState} from 'react';
import {DASHBOARD_DATE_PRESETS, CUSTOM_RANGE_ID} from '../constants/dashboardDatePresets';
import type {DashboardGroupByToken} from '../utils/groupTickets';

export type DashboardDateField = 'created_at' | 'last_activity_at' | 'resolved_at';

export interface DashboardFilters {
	dateField: DashboardDateField;
	rangePresetId: string | null;
	from: string;
	to: string;
	/**
	 * The active grouping. Client-side only — grouping re-lays-out the loaded rows
	 * and is deliberately *not* part of the analytics query variables (PRD §13), so
	 * changing it must not refetch.
	 */
	groupBy: DashboardGroupByToken;
}

export interface UseDashboardFilters {
	filters: DashboardFilters;
	setDateField: (field: DashboardDateField) => void;
	setRangePreset: (presetId: string) => void;
	setCustomRange: (from: string, to: string) => void;
	setGroupBy: (groupBy: DashboardGroupByToken) => void;
}

function getDefaultRange(): {from: string; to: string} {
	const last30 = DASHBOARD_DATE_PRESETS.find(p => p.id === 'last_30_days');
	return last30 ? last30.getRange() : {from: '', to: ''};
}

export function useDashboardFilters(): UseDashboardFilters {
	const defaultRange = useMemo(() => getDefaultRange(), []);

	const [filters, setFilters] = useState<DashboardFilters>({
		dateField: 'created_at',
		rangePresetId: 'last_30_days',
		from: defaultRange.from,
		to: defaultRange.to,
		groupBy: 'none',
	});

	const setDateField = useCallback((field: DashboardDateField) => {
		setFilters(prev => ({...prev, dateField: field}));
	}, []);

	const setRangePreset = useCallback((presetId: string) => {
		if (presetId === CUSTOM_RANGE_ID) {
			setFilters(prev => ({...prev, rangePresetId: CUSTOM_RANGE_ID}));
			return;
		}
		const preset = DASHBOARD_DATE_PRESETS.find(p => p.id === presetId);
		if (!preset) return;
		const {from, to} = preset.getRange();
		setFilters(prev => ({...prev, rangePresetId: presetId, from, to}));
	}, []);

	const setCustomRange = useCallback((from: string, to: string) => {
		setFilters(prev => ({...prev, rangePresetId: CUSTOM_RANGE_ID, from, to}));
	}, []);

	const setGroupBy = useCallback((groupBy: DashboardGroupByToken) => {
		setFilters(prev => ({...prev, groupBy}));
	}, []);

	return useMemo(
		() => ({filters, setDateField, setRangePreset, setCustomRange, setGroupBy}),
		[filters, setDateField, setRangePreset, setCustomRange, setGroupBy]
	);
}
